import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../components/cards/PageHeader";
import { AppColors } from "../../constants/AppColors";
import {
  BillingPlanPriceResponse,
  BillingPlanResponse,
} from "../../models/ResponseModels/BillingPlanResponse";
import { useSubscriptionScreen } from "./hooks/useSubscriptionScreen";
import MerchantInformationCard from "../../components/subscription/MerchantInformationCard";
import { BAM_SUBSCRIPTION_PRICES } from "../../constants/BamSubscriptionPrices";

const getPeriodLabel = (periodMonths: number) => {
  if (periodMonths === 1) {
    return "Mjesečno";
  }

  if (periodMonths === 6) {
    return "6 mjeseci";
  }

  if (periodMonths === 12) {
    return "12 mjeseci";
  }

  return `${periodMonths} mjeseci`;
};

const getPlanPrice = (
  plan: BillingPlanResponse,
  periodMonths: number,
): BillingPlanPriceResponse | undefined => {
  return plan.prices.find((price) => price.period_months === periodMonths);
};

const getDisplayedPrice = (
  plan: BillingPlanResponse,
  price: BillingPlanPriceResponse,
  language: string,
): string => {
  if (language === "bs" && plan.property_limit !== null) {
    const bamPrice =
      BAM_SUBSCRIPTION_PRICES[plan.property_limit]?.[price.period_months];

    if (bamPrice) {
      return `${bamPrice} BAM`;
    }
  }

  return `${price.display_price} ${price.currency}`;
};

const getLimitLabel = (plan: BillingPlanResponse) => {
  if (plan.property_limit === 1) {
    return "1 objekat";
  }

  if (plan.property_limit === 5) {
    return "2–5 objekata";
  }

  if (plan.property_limit === 10) {
    return "6–10 objekata";
  }

  if (plan.property_limit === 20) {
    return "11–20 objekata";
  }

  if (plan.property_limit === null) {
    return "Neograničeno";
  }

  return `Do ${plan.property_limit} objekata`;
};

const SubscriptionScreen = () => {
  const vm = useSubscriptionScreen();
  const currentPlan = vm.account?.subscription_plan;
  const currentPlanDetails = vm.plans.find((plan) => plan.code === currentPlan);

  return (
    <Box>
      <PageHeader
        title={vm.t("subscription.title")}
        description={vm.t("subscription.subtitle")}
        action={
          <Button variant="outlined" onClick={vm.manageBilling}>
            {vm.t("subscription.manageBilling")}
          </Button>
        }
      />

      {vm.loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      {vm.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {vm.error}
        </Alert>
      ) : null}

      <Card sx={{ mb: 3, border: `1px solid ${AppColors.primarySoft}` }}>
        <CardContent>
          <Typography color="text.secondary">
            {vm.t("subscription.currentPlan")}
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            {vm.account?.subscription_name ||
              currentPlanDetails?.name ||
              "Free"}
          </Typography>

          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {vm.account?.readable_subscription_status}
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              justifyContent: "space-between",
              alignItems: {
                xs: "stretch",
                sm: "center",
              },
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {vm.t("subscription.billingPeriod")}
              </Typography>

              <Typography color="text.secondary">
                {vm.t("subscription.billingPeriodDescription")}
              </Typography>
            </Box>

            <ToggleButtonGroup
              exclusive
              value={vm.selectedPeriodMonths}
              onChange={(_, value) => {
                if (value) {
                  vm.setSelectedPeriodMonths(Number(value));
                }
              }}
              size="small"
              sx={{
                alignSelf: {
                  xs: "stretch",
                  sm: "center",
                },
              }}
            >
              {vm.availablePeriods.map((periodMonths) => (
                <ToggleButton
                  key={periodMonths}
                  value={periodMonths}
                  sx={{
                    flex: {
                      xs: 1,
                      sm: "initial",
                    },
                    px: 2,
                  }}
                >
                  {getPeriodLabel(periodMonths)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{vm.t("subscription.planColumn")}</TableCell>
                  <TableCell>{vm.t("subscription.limitColumn")}</TableCell>
                  <TableCell>{vm.t("subscription.priceColumn")}</TableCell>
                  <TableCell>{vm.t("subscription.discountColumn")}</TableCell>
                  <TableCell>{vm.t("subscription.featuresColumn")}</TableCell>
                  <TableCell align="right">
                    {vm.t("subscription.actionColumn")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {vm.plans.map((plan) => {
                  const price = getPlanPrice(plan, vm.selectedPeriodMonths);
                  const isCurrent = currentPlan === plan.code;

                  if (!price) {
                    return null;
                  }

                  return (
                    <TableRow
                      key={plan.id}
                      sx={{
                        backgroundColor: plan.highlighted
                          ? AppColors.primarySoft
                          : "inherit",
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography sx={{ fontWeight: 900 }}>
                              {plan.name}
                            </Typography>

                            {plan.highlighted ? (
                              <Chip
                                size="small"
                                color="primary"
                                label={vm.t("subscription.recommended")}
                              />
                            ) : null}

                            {isCurrent ? (
                              <Chip
                                size="small"
                                color="success"
                                label={vm.t("common.current")}
                              />
                            ) : null}
                          </Box>

                          <Typography variant="body2" color="text.secondary">
                            {plan.description}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontWeight: 800 }}>
                          {getLimitLabel(plan)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                          {getDisplayedPrice(plan, price, vm.language)}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {price.billing_label}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {price.discount_percent > 0 ? (
                          <Chip
                            size="small"
                            color="success"
                            label={vm.t("subscription.discountBadge", {
                              discount: price.discount_percent,
                            })}
                          />
                        ) : (
                          <Typography color="text.secondary">—</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {plan.features.slice(1).map((feature) => (
                            <Typography key={feature} variant="body2">
                              ✓ {feature}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Button
                          variant={plan.highlighted ? "contained" : "outlined"}
                          disabled={isCurrent || vm.loading}
                          onClick={() => vm.choosePrice(price.id)}
                        >
                          {isCurrent
                            ? vm.t("common.current")
                            : vm.t("subscription.choosePlan")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <MerchantInformationCard />
    </Box>
  );
};

export default SubscriptionScreen;
